import { SITE } from '@/app/constants';
import { calculateDaysUntilUTC } from '@/app/utils';
import { ClawContentBanner } from './components/ClawContentBanner';
import { RetentionBanner } from './components/RetentionBanner';
import { ViduS1Banner } from './components/ViduS1Banner';
import type { AnnouncementConfig } from './types';

/**
 * 顶部公告注册表
 *
 * 调度规则：
 * - 同一时间只展示一条公告，取 priority 最高且满足条件的
 * - priority 值越大优先级越高；新活动想覆盖旧活动，给更高 priority 即可
 * - expiresAt（可选）：UTC 过期时间，到期后自动隐藏；省略表示长驻
 * - condition（可选）：运行时条件函数，不满足时跳过，自动 fallback 到下一条
 * - 用户点击关闭后永久隐藏（localStorage），不影响其他公告
 *
 * 新增公告：在 components/ 下创建 Banner 组件，然后在此数组追加一条配置
 */
export const ANNOUNCEMENTS: AnnouncementConfig[] = [
  /**
   * @popupTitle Vidu S1 顶部横幅
   * @popupSite both
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   * @popupCondition 全量展示
   * @popupFigma https://www.figma.com/design/SIDn1gNJEGhPMgKzkfZueQ/%E3%80%90SaaS-web%E3%80%913.0?node-id=38030-1518&m=dev
   */
  {
    id: 'vidu_s1_stream_banner',
    priority: 400,
    component: (props) => <ViduS1Banner {...props} />,
  },
  // Claw 推广内容 banner - 长驻；团队空间下不展示
  {
    id: 'claw_promotion_content_banner',
    priority: 300,
    component: (props) => <ClawContentBanner {...props} />,
    condition: (ctx) => !ctx.isTeamSpace,
  },
  /**
   * @popupTitle 取消续费挽留横幅 - 最后一天
   * @popupSite china
   * @popupCondition 用户已取消续费 + 未处于提前续费冷却期 + 距到期 1 天内
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   * @popupNote 优先级最高，覆盖临期 7 天横幅
   */
  {
    id: 'retention_last_day',
    priority: 210,
    component: (props) => <RetentionBanner {...props} phase="lastDay" />,
    condition: (ctx) => {
      if (SITE !== 'china') return false;
      const sub = ctx.subscription;
      if (!sub?.auto_renew_canceled || sub.advance_renew_in_cooldown || !sub.end_time) return false;
      const days = calculateDaysUntilUTC(sub.end_time);
      return days > 0 && days <= 1;
    },
  },
  /**
   * @popupTitle 取消续费挽留横幅 - 临期 7 天
   * @popupSite china
   * @popupCondition 用户已取消续费 + 未处于提前续费冷却期 + 距到期 1~7 天
   * @popupFrequency 长驻；用户点击关闭后永久隐藏
   */
  {
    id: 'retention_expiring_7d',
    priority: 200,
    component: (props) => <RetentionBanner {...props} phase="expiring" />,
    condition: (ctx) => {
      if (SITE !== 'china') return false;
      const sub = ctx.subscription;
      if (!sub?.auto_renew_canceled || sub.advance_renew_in_cooldown || !sub.end_time) return false;
      const days = calculateDaysUntilUTC(sub.end_time);
      return days > 1 && days <= 7;
    },
  },
];
